import { AdminAppEventRow, AdminSortOrder } from '@orbit/api-types'
import { useAdminUserAppEvents } from '@hooks/useAdminUserAppEvents'
import React, { useState } from 'react'
import { formatDateTimeWithSeconds } from '../adminFormat'
import { Card, CardTitle } from '../adminDetail.styles'
import {
	Muted,
	PageButton,
	PageStatus,
	Pagination,
	Row,
	Table,
	Td,
	Th,
} from '../adminList.styles'
import {
	EventTypeCell,
	FeedHeader,
	FeedTableWrap,
	PayloadBlock,
	PayloadPreview,
	SortButton,
} from './UserAppEvents.styles'
import { EmptyNoteInline, EmptyNote } from '@components/EmptyState'

const PAGE_SIZE = 25

/** The device an event came from, as one line: "ios 18.2 · iPhone15,3". */
const formatDevice = (event: AdminAppEventRow): string =>
	[event.platform, event.osVersion, event.deviceModel].filter(Boolean).join(' · ')

/**
 * The app version as the emitter reported it, with the platform's build identifier
 * appended when there is one — Android sends `versionCode`, iOS `buildNumber`.
 */
const formatVersion = (event: AdminAppEventRow): string => {
	const build = event.versionCode ?? event.buildNumber
	return build != null ? `${event.appVersion} (${build})` : event.appVersion
}

/**
 * The account's analytics feed: a paginated, sortable table of every `app_events`
 * row attributed to this user. Independent of the admin layout's date filter — this
 * is the whole history — so it owns its own page and sort state, and flipping the
 * sort restarts at page 1 so the newest/oldest end is what you actually land on.
 *
 * A row with a payload expands in place to pretty-print it; collapsed rows show a
 * truncated one-liner, so a noisy `data` blob never stretches the table.
 */
const UserAppEvents: React.FC<{ userId: number }> = ({ userId }) => {
	const [page, setPage] = useState(1)
	const [sort, setSort] = useState<AdminSortOrder>('desc')
	const [expandedId, setExpandedId] = useState<number | null>(null)

	const { data, isLoading, isError } = useAdminUserAppEvents({
		userId,
		page,
		pageSize: PAGE_SIZE,
		sort,
	})

	const toggleSort = () => {
		setSort(current => (current === 'desc' ? 'asc' : 'desc'))
		setPage(1)
		setExpandedId(null)
	}

	const goToPage = (next: number) => {
		setPage(next)
		setExpandedId(null)
	}

	return (
		<Card>
			<FeedHeader>
				<CardTitle>App events{data ? ` (${data.total.toLocaleString()})` : ''}</CardTitle>
				<SortButton type="button" onClick={toggleSort} disabled={isLoading || isError}>
					{sort === 'desc' ? 'Newest first ↓' : 'Oldest first ↑'}
				</SortButton>
			</FeedHeader>

			{isLoading ? (
				<EmptyNoteInline>Loading events…</EmptyNoteInline>
			) : isError || !data ? (
				<EmptyNoteInline>Could not load this user&rsquo;s events.</EmptyNoteInline>
			) : data.events.length === 0 ? (
				<EmptyNoteInline>This user has not emitted any events.</EmptyNoteInline>
			) : (
				<>
					<FeedTableWrap>
						<Table>
							<thead>
								<tr>
									<Th>Time</Th>
									<Th>Event</Th>
									<Th>Version</Th>
									<Th>Device</Th>
									<Th>Data</Th>
								</tr>
							</thead>
							<tbody>
								{data.events.map(event => {
									const isExpanded = expandedId === event.id
									const payload = event.data
									return (
										<React.Fragment key={event.id}>
											<Row
												onClick={() => setExpandedId(isExpanded ? null : event.id)}
												aria-expanded={isExpanded}
											>
												<Td>{formatDateTimeWithSeconds(event.timestamp)}</Td>
												<Td>
													<EventTypeCell>{event.eventType}</EventTypeCell>
												</Td>
												<Td>{formatVersion(event)}</Td>
												<Td>{formatDevice(event) || <Muted>—</Muted>}</Td>
												<Td>
													{payload ? (
														<PayloadPreview>{JSON.stringify(payload)}</PayloadPreview>
													) : (
														<Muted>—</Muted>
													)}
												</Td>
											</Row>
											{isExpanded && (
												<tr>
													<Td colSpan={5}>
														{payload ? (
															<PayloadBlock>{JSON.stringify(payload, null, 2)}</PayloadBlock>
														) : (
															<EmptyNote>This event carries no data payload.</EmptyNote>
														)}
													</Td>
												</tr>
											)}
										</React.Fragment>
									)
								})}
							</tbody>
						</Table>
					</FeedTableWrap>

					<Pagination>
						<PageStatus>
							Page {data.page} of {data.totalPages}
						</PageStatus>
						<PageButton disabled={data.page <= 1} onClick={() => goToPage(Math.max(1, page - 1))}>
							Previous
						</PageButton>
						<PageButton disabled={data.page >= data.totalPages} onClick={() => goToPage(page + 1)}>
							Next
						</PageButton>
					</Pagination>
				</>
			)}
		</Card>
	)
}

export default UserAppEvents
